import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Select from 'antd/lib/select';
import { UserOrgsResponse } from '../../apiResponseTypes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import * as API from '../../api';
import { selectAuthCredentials } from '../../selectors/selectors';
import { tryChangeOrgFetchState, TRY_CHANGE_ORG_FETCH_STATE } from '../../actions/state';
import Form from 'antd/lib/form';
import { useHistory } from 'react-router-dom';
import { forEach } from 'lodash';
import { createOrgRoute } from '../../helpers/routeHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useWillUpdate } from '@/utils/hooks/helpers';
import { STAPE_ID_COOKIE_KEY } from '@/constants';
import * as Cookie from 'cookie';
import { hubSpotUserToken } from '@/helpers';

interface ChangeOrgModalProps {
    open: boolean;
    currentOrgId: string;
    onCancel: () => void;
    onSuccess: () => void;
}

const ChangeOrgModal: React.FC<ChangeOrgModalProps> = (props: ChangeOrgModalProps) => {
    const userId = useSelector((state: RootState) => state.user.id);
    const authCredentials = useSelector((state: RootState) => selectAuthCredentials(state));
    const history = useHistory();

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const cookies = Cookie.parse(document.cookie);

    const [orgs, setOrgs] = React.useState<Record<string, UserOrgsResponse>>({});
    const [selectedOrg, setSelectedOrg] = React.useState(null);

    const stateWillUpdate = useWillUpdate(TRY_CHANGE_ORG_FETCH_STATE);
    const stateFetchIsLoading = useIsLoading(TRY_CHANGE_ORG_FETCH_STATE);
    const stateFetchError = useError(TRY_CHANGE_ORG_FETCH_STATE);

    const onOkChangeOrg = (values) => {
        const orgId = values.selectedOrgId;

        dispatch(
            tryChangeOrgFetchState({
                orgId,
                userId,
                hubspotUserToken: hubSpotUserToken(document),
                stapeId: cookies[STAPE_ID_COOKIE_KEY],
            }),
        );
    };

    const updateOrgs = (orgsData: UserOrgsResponse[]) => {
        const updatedOrgs = {};

        forEach(orgsData, (userOrgData) => {
            updatedOrgs[userOrgData.id] = userOrgData;
        });

        setOrgs(updatedOrgs);
    };

    React.useEffect(() => {
        if (props.open) {
            // fetch user orgs
            API.getOrgsForUser(authCredentials)
                .then((orgs) => {
                    updateOrgs(orgs);
                })
                .catch((e) => globalMessage.error(e.message));
        }
    }, [props.open]);

    React.useEffect(() => {
        if (stateWillUpdate && selectedOrg) {
            props.onSuccess();

            const orgRoute = createOrgRoute(selectedOrg);

            console.debug('[REDIRECT DEBUG]', 'stateWillUpdate', 'redirecting to', orgRoute);

            history.replace(orgRoute);
        }
    }, [stateWillUpdate]);

    React.useEffect(() => {
        if (stateFetchError) {
            globalMessage.error(stateFetchError);
        }
    }, [stateFetchError]);

    return (
        <Modal
            open={props.open}
            title="Switch organization"
            okText="Switch"
            okButtonProps={{ loading: stateFetchIsLoading }}
            onOk={() => form.submit()}
            onCancel={props.onCancel}
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text type="secondary">Select from the list of organizations your user belongs to.</Text>
                <Form
                    form={form}
                    initialValues={{ selectedOrgId: props.currentOrgId }}
                    style={{ marginTop: 24 }}
                    onFinish={onOkChangeOrg}
                >
                    <Form.Item name="selectedOrgId" rules={[{ required: true }]}>
                        <Select
                            placeholder="Select"
                            showSearch
                            filterOption={(inputValue, option) => {
                                const value = option.value;
                                const org = orgs[value];
                                const searchString = [org.orgId].concat(org.apps).join(', ');

                                return searchString.includes(inputValue);
                            }}
                            onSelect={(value) => setSelectedOrg(value)}
                        >
                            {Object.keys(orgs).map((orgId, index) => {
                                const org = orgs[orgId];

                                return (
                                    <Select.Option key={`${org.id}-${index}`} value={org.id}>
                                        <div style={{ display: 'flex' }}>
                                            <Text strong>{org.orgId}</Text>
                                            {org.apps.length > 0 && <Text ellipsis> - ({org.apps.join(', ')})</Text>}
                                        </div>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

export default ChangeOrgModal;
